'use client';

import { IconDoubleChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-double-chevron-right-filled';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PaginationRootContext } from './context/pagination-root.context';
import type { PaginationJumpToLastButtonPropsInterface } from './types/pagination-jump-to-last-button.types';

/**
 * The jump-to-last-page `button` element
 * @since 10.17.0
 */
export const PaginationJumpToLastButton = forwardRef<
  HTMLButtonElement,
  PaginationJumpToLastButtonPropsInterface
>((props: PaginationJumpToLastButtonPropsInterface, forwardedRef) => {
  const {
    children = (
      <Icon>
        <IconDoubleChevronRightFilled />
      </Icon>
    ),
    onClick,
    ...rest
  } = props;
  const { setPageIndex, currentPageIndex = 0, totalPages } = useContext(PaginationRootContext);

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setPageIndex?.(totalPages - 1);
      onClick?.(event);
    },
    [setPageIndex, totalPages, onClick],
  );

  return (
    <IconButton
      data-c11n-component="Pagination.JumpToLastButton"
      title="Last page"
      tone="neutral"
      shape="circle"
      emphasis="bare"
      size="sm"
      onClick={handleClick}
      disabled={currentPageIndex >= totalPages - 1}
      ref={forwardedRef}
      rel="last"
      {...rest}
    >
      {children}
    </IconButton>
  );
});

PaginationJumpToLastButton.displayName = 'Pagination.JumpToLastButton';
